import { ActionCreator } from 'redux';

import { Contact, ContactPattern, SuppressedStatus } from '@dev/ee-api/ee-api-models';
import { isString } from 'lodash-es';

import {
    SuppressionPayload,
    StartLoadingSuppressionsAction,
    SuppressionsLoadingErrorAction,
    SuppressionsLoadingSuccessAction,
    LoadSuppressionsAction,
    AddSuppressionAction,
    AddSuppressionErrorAction,
    AddSuppressionStartAction,
    AddSuppressionSuccessAction,
    FindSuppressionsAction,
    ContactStatusMap,
    ContactSourceMap,
    ConsentTrackingMap,
    ContactCounts,
    DeleteSuppressionAction,
    DeleteSuppressionErrorAction,
    DeleteSuppressionStartAction,
    DeleteSuppressionSuccessAction,
    ExportSuppressionAction,
    ExportSuppressionErrorAction,
    ExportSuppressionStartAction,
    ExportSuppressionSuccessAction,
    UploadContactsAction,
    GetContactsPatternAction,
    GetContactsPatternStartAction,
    GetContactsPatternErrorAction,
    GetContactsPatternSuccessAction,
    DeleteSuppresionsListAction,
    CloseSupperssionsScreenAction,
    SuppressionsActionTypes,
    GetContactsUnsubscribedOverviewAction,
    SuppressionsUnsubscribedContactsInterface,
    GetContactsUnsubscribedOverviewSuccessAction,
    GetContactsUnsubscribedOverviewErrorAction,
    GetContactsUnsubscribedAction,
    GetContactsUnsubscribedSuccessAction,
    GetContactsUnsubscribedErrorAction,
    SuppressionsContactsUnsubscribedParams,
    ExportContactsUnsubscribedAction,
    SetPickedContactEmailAction,
    ClearContactsPatternAction,
} from './types';

import { ExportContactsUnsubscribedParams, ExportSuppressionsParams } from '@api/services/ElasticApi';
import { store } from '@shared-store/shared-store';
import { ApplicationState } from '..';

const parseContact = (contact: Contact): Contact => {
    if (!isString(contact.Status)) {
        if (!ContactStatusMap['' + contact.Status]) {
            throw Error(`Unknown contact status`);
        }
        contact.Status = ContactStatusMap['' + contact.Status];
    }
    if (!isString(contact.Source)) {
        if (!ContactSourceMap['' + contact.Source]) {
            throw Error(`Unknown contact source`);
        }
        contact.Source = ContactSourceMap['' + contact.Source];
    }
    if (!isString(contact.ConsentTracking)) {
        if (!ConsentTrackingMap['' + contact.ConsentTracking]) {
            throw Error(`Unknown consent status`);
        }
        contact.ConsentTracking = ConsentTrackingMap['' + contact.ConsentTracking];
    }

    return contact;
};

export const UploadSuppression: ActionCreator<UploadContactsAction> = (file: File, errorModal: JSX.Element) => {
    return {
        type: SuppressionsActionTypes.UPLOAD_FILES,
        payload: {
            file,
            errorModal,
        },
    };
};

export const DeleteSuppressions: ActionCreator<DeleteSuppressionAction> = (emails: string[], selectedAll?: boolean) => ({
    type: SuppressionsActionTypes.DELETE_SUPPRESSION,
    payload: {
        emails,
        selectedAll,
    },
});

export const DeleteSuppressionStart: ActionCreator<DeleteSuppressionStartAction> = () => ({
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_START,
});

export const DeleteSuppressionError: ActionCreator<DeleteSuppressionErrorAction> = () => ({
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_ERROR,
});

export const DeleteSuppressionSuccess: ActionCreator<DeleteSuppressionSuccessAction> = () => ({
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_SUCCESS,
});

export const FindSuppressions: ActionCreator<FindSuppressionsAction> = (params?: SuppressionPayload) => {
    const activePage = store.getState().suppressions.activePage;

    if (activePage > 1) {
        params.activePage = 1;
    }

    return {
        type: SuppressionsActionTypes.SEARCH_FOR_CONTACT,
        payload: params,
    };
};

export const LoadSuppressions: ActionCreator<LoadSuppressionsAction> = (params?: SuppressionPayload) => {
    return {
        type: SuppressionsActionTypes.FETCH_CONTACTS,
        payload: params,
    };
};

export const StartLoadingSuppressions: ActionCreator<StartLoadingSuppressionsAction> = () => ({
    type: SuppressionsActionTypes.FETCH_CONTACTS_START,
});

export const SuppressionsLoadingError: ActionCreator<SuppressionsLoadingErrorAction> = () => ({
    type: SuppressionsActionTypes.FETCH_CONTACTS_ERROR,
});

export const SuppressionsLoaded: ActionCreator<SuppressionsLoadingSuccessAction> = (contacts: Contact[], counts: ContactCounts) => ({
    type: SuppressionsActionTypes.FETCH_CONTACTS_SUCCESS,
    payload: {
        contacts: contacts.map((contact) => parseContact(contact)),
        counts,
    },
});

export const AddSuppression: ActionCreator<AddSuppressionAction> = (status: SuppressedStatus, emails?: string[]) => ({
    type: SuppressionsActionTypes.CREATE_SUPPRESSION,
    payload: {
        emails,
        status,
    },
});

export const AddSuppressionStart: ActionCreator<AddSuppressionStartAction> = () => ({
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_START,
});

export const AddSuppressionSuccess: ActionCreator<AddSuppressionSuccessAction> = (contacts: Contact[]) => ({
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_SUCCESS,
    payload: {
        contacts: contacts.map((contact) => parseContact(contact)),
    },
});

export const AddSuppressionError: ActionCreator<AddSuppressionErrorAction> = () => ({
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_ERROR,
});

export const ExportSuppression: ActionCreator<ExportSuppressionAction> = (params: ExportSuppressionsParams) => ({
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS,
    payload: params,
});

export const ExportSuppressionStart: ActionCreator<ExportSuppressionStartAction> = () => ({
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_START,
});

export const ExportSuppressionError: ActionCreator<ExportSuppressionErrorAction> = () => ({
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_ERROR,
});

export const ExportSuppressionSuccess: ActionCreator<ExportSuppressionSuccessAction> = () => ({
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_SUCCESS,
});

export const GetContactsPattern: ActionCreator<GetContactsPatternAction> = (fileName: string, errorModal: JSX.Element) => ({
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN,
    payload: { fileName, errorModal },
});

export const GetContactsPatternStart: ActionCreator<GetContactsPatternStartAction> = () => ({
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_START,
});

export const GetContactsPatternSuccess: ActionCreator<GetContactsPatternSuccessAction> = (
    contactsPattern: ContactPattern,
    fileName: string
) => ({
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_SUCCESS,
    payload: {
        fileName,
        contactsPattern,
    },
});

export const GetContactsPatternError: ActionCreator<GetContactsPatternErrorAction> = () => ({
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_ERROR,
});

export const ClearContactsPattern: ActionCreator<ClearContactsPatternAction> = () => ({
    type: SuppressionsActionTypes.CLEAR_CONTACTS_PATTERN,
});

export const DeleteSuppresionsList: ActionCreator<DeleteSuppresionsListAction> = () => ({
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_LIST,
    payload: {
        fileName: store.getState().suppressions.fileName || store.getState().upload.fileuploaded.FileName,
    },
});

export const CloseSupperssionsScreen: ActionCreator<CloseSupperssionsScreenAction> = () => ({
    type: SuppressionsActionTypes.CLEAR_DATA,
});

export const GetContactsUnsubscribedOverview = (from: string, to: string): GetContactsUnsubscribedOverviewAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW,
    payload: {
        from,
        to,
    },
});

export const GetContactsUnsubscribedOverviewSuccess = (
    unsubscribedContacts: SuppressionsUnsubscribedContactsInterface
): GetContactsUnsubscribedOverviewSuccessAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS,
    payload: unsubscribedContacts,
});

export const GetContactsUnsubscribedOverviewError = (): GetContactsUnsubscribedOverviewErrorAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR,
});

export const GetContactsUnsubscribed = (params: SuppressionsContactsUnsubscribedParams): GetContactsUnsubscribedAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED,
    payload: {
        ...params,
    },
});
export const GetContactsUnsubscribedSuccess = (
    unsubscribedContacts: SuppressionsUnsubscribedContactsInterface
): GetContactsUnsubscribedSuccessAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS,
    payload: unsubscribedContacts,
});
export const GetContactsUnsubscribedError = (): GetContactsUnsubscribedErrorAction => ({
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR,
});

export const ExportContactsUnsubscribed: ActionCreator<ExportContactsUnsubscribedAction> = (
    params: ExportContactsUnsubscribedParams
): ExportContactsUnsubscribedAction => ({
    type: SuppressionsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED,
    payload: params,
});

export const SetPickedContactEmail: ActionCreator<SetPickedContactEmailAction> = (
    pickedContactEmail?: string
): SetPickedContactEmailAction => ({
    type: SuppressionsActionTypes.SET_PICKED_CONTACT_EMAIL,
    payload: {
        pickedContactEmail,
    },
});

export const getUnsubscribeContactsStore = (state: ApplicationState): SuppressionsUnsubscribedContactsInterface =>
    state.suppressions.unsubscribedContacts;

export const getSuppressionsTotalCountStore = (state: ApplicationState): number => state.suppressions.counts.totalCount;
